import React, { useMemo } from "react";
import "./ApplyAllModal.css";

export default function ApplyAllModal({ open, onClose, product, results, dimensions = {}, onConfirm, isSingleVariant = false }) {
  // Calculate product-level price impact
  const productPriceImpact = useMemo(() => {
    if (!product || !results || results.length === 0) {
      return { ds: {}, ws: {} };
    }
    const variations = product?.variations || [];
    
    // Calculate old min DS final price
    const oldDsPrices = variations
      .map(v => v.finalPrice)
      .filter(p => p != null && Number.isFinite(p));
    const oldMinDsPrice = oldDsPrices.length > 0 ? Math.min(...oldDsPrices) : null;
    const oldDsMrp = oldMinDsPrice ? oldMinDsPrice * 2.5 : null;
    
    // Calculate old min WS final price
    const oldWsPrices = variations
      .map(v => v.wholesaleFinalPrice)
      .filter(p => p != null && Number.isFinite(p));
    const oldMinWsPrice = oldWsPrices.length > 0 ? Math.min(...oldWsPrices) : null;
    const oldWsMrp = oldMinWsPrice ? oldMinWsPrice * 2.5 : null;
    
    // Calculate new min DS final price from results
    const newDsPrices = results
      .map(r => r.finalPrice)
      .filter(p => p != null && Number.isFinite(p));
    const newMinDsPrice = newDsPrices.length > 0 ? Math.min(...newDsPrices) : null;
    const newDsMrp = newMinDsPrice ? newMinDsPrice * 2.5 : null;
    
    // Calculate new min WS final price from results
    const newWsPrices = results
      .map(r => r.wholesaleFinalPrice)
      .filter(p => p != null && Number.isFinite(p));
    const newMinWsPrice = newWsPrices.length > 0 ? Math.min(...newWsPrices) : null;
    const newWsMrp = newMinWsPrice ? newMinWsPrice * 2.5 : null;
    
    return {
      ds: {
        oldSp: oldMinDsPrice ? Math.round(oldMinDsPrice) : null,
        newSp: newMinDsPrice ? Math.round(newMinDsPrice) : null,
        oldMrp: oldDsMrp ? Math.round(oldDsMrp) : null,
        newMrp: newDsMrp ? Math.round(newDsMrp) : null,
      },
      ws: {
        oldSp: oldMinWsPrice ? Math.round(oldMinWsPrice) : null,
        newSp: newMinWsPrice ? Math.round(newMinWsPrice) : null,
        oldMrp: oldWsMrp ? Math.round(oldWsMrp) : null,
        newMrp: newWsMrp ? Math.round(newWsMrp) : null,
      }
    };
  }, [product, results]);

  // Build preview rows - combine results with actual variant data
  const previewRows = useMemo(() => {
    if (!product || !results || results.length === 0) {
      return [];
    }
 
    const normalizeVariantImage = (variant) => {
      if (!variant) return "";
      
      // Use the same pattern as ProductErrorDetail: imgUrl[0] || image || fallback
      if (Array.isArray(variant.imgUrl) && variant.imgUrl.length > 0) {
        return variant.imgUrl[0];
      }
 
      if (typeof variant.imgUrl === "string" && variant.imgUrl) {
        return variant.imgUrl;
      }
 
      if (variant.image) {
        return variant.image;
      }
 
      return "";
    };
 
    return results.map((result) => {
      const variantData = result.variant || {};

      return {
        skuId: result.variantSkuId || result.variantKey || "-",
        image : variantData.skuAttributes[0].thumbnailImage || variantData.skuAttributes[0].skuImageUrl || "",
        weight: dimensions.weight ?? variantData.weight ?? 0,
        length: dimensions.length ?? variantData.length ?? 0,
        width: dimensions.width ?? variantData.width ?? 0,
        height: dimensions.height ?? variantData.height ?? 0,
        finalPrice: result.finalPrice ? Math.round(result.finalPrice) : 0,
        wholesaleFinalPrice: result.wholesaleFinalPrice ? Math.round(result.wholesaleFinalPrice) : 0,
      };
    });
  }, [product, results, dimensions]);

  // Early return after all hooks
  if (!open || !results || results.length === 0) return null;

  const normalizeUrl = (url) => {
    if (!url) return "";
    if (String(url).startsWith("http://")) {
      return String(url).replace(/^http:\/\//, "https://");
    }
    if (String(url).startsWith("https://")) {
      return url;
    }
    return `https://images-na.ssl-images-amazon.com/images/I/${url}`;
  };

  const formatPrice = (value, symbol = "₹") => {
    if (value == null || !Number.isFinite(value)) return "-";
    return `${symbol}${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleConfirm = () => {
    onConfirm(results, dimensions);
  };

  return (
    <div className="aam-overlay">
      <div className="aam-modal">
        {/* Header */}
        <div className="aam-header">
          <h2>{isSingleVariant ? 'Apply Final Price to Variant - Preview' : 'Apply to All Variants - Preview'}</h2>
          <button className="aam-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Dimensions Applied to All */}
        <div className="aam-dimensions-section">
          <div className="aam-dimensions-grid">
            <div className="aam-dimension-row">
              <span className="aam-dimension-label">{isSingleVariant ? 'Dimensions Applied to Variant' : 'Dimensions Applied to All Variants'}</span>
              <span className="aam-dimension-label">Weight:</span>
              <span className="aam-dimension-value">{dimensions.weight || 0} kg</span>
              <span className="aam-dimension-label">Length:</span>
              <span className="aam-dimension-value">{dimensions.length || 0} cm</span>
              <span className="aam-dimension-label">Width:</span>
              <span className="aam-dimension-value">{dimensions.width || 0} cm</span>
              <span className="aam-dimension-label">Height:</span>
              <span className="aam-dimension-value">{dimensions.height || 0} cm</span>
              <span className="aam-dimension-label">Selling Price (SP):</span>
              <span className="aam-dimension-value">
                {formatPrice(productPriceImpact.ds.oldSp)} {" → "} {formatPrice(productPriceImpact.ds.newSp)}
              </span>
            </div>
          </div>
        </div>

        {/* Per-Variant Preview Table */}
        <div className="aam-table-section">
          <h3>Per-Variant Changes ({previewRows.length} variants)</h3>
          <div className="aam-table-container">
            <table className="aam-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>SKU ID</th>
                  <th>Weight</th>
                  <th>Dimensions</th>
                  <th>DS Final Price</th>
                  <th>WS Final Price</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, index) => {
                  const normalizeUrl = (url) => url ? (url.startsWith('http') ? url : `https://images-na.ssl-images-amazon.com/images/I/${url}`) : "";
                  
                  return (
                    <tr key={index}>
                      <td className="aam-image-cell">
                        {row.image ? (
                          <img
                            src={normalizeUrl(row.image)}
                            alt={row.skuId}
                            className="aam-variant-image"
                            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                          />
                        ) : (
                          <div className="aam-no-image">-</div>
                        )}
                      </td>
                      <td className="aam-sku-cell">{row.skuId}</td>
                      <td>{row.weight} kg</td>
                      <td>{row.length} × {row.width} × {row.height} cm</td>
                      <td className="aam-new-cell">{formatPrice(row.finalPrice)}</td>
                      <td className="aam-new-cell">{formatPrice(row.wholesaleFinalPrice)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="aam-footer">
          <button className="aam-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="aam-confirm-btn" onClick={handleConfirm}>
            {isSingleVariant ? 'Confirm & Apply to Variant' : 'Confirm & Apply to All Variants'}
          </button>
        </div>
      </div>
    </div>
  );
}
